import { Box, Fade, GridLegacy as Grid } from '@mui/material';
import { FC, createRef, useEffect } from 'react';

import { useMovieFinder, useSnack } from '@/logic/state';

import { FinderFiltersResponse } from '@/logic/services/movie-finder';
import { FinderMovieCard } from './movie-card';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useIntl } from 'react-intl';
import { useScrollToItem } from '@/logic/hooks';
import ImageBoxFuture from '../shared/image-box-future';
import { MovieEmptyState } from '../shared/movie-empty-state';
import { MovieListLoader } from '../shared/movie-list-loader';

const notAllowedIcon = '/icons/shared/not-allowed-icon.svg';

type MoviesProps = Pick<FinderFiltersResponse, 'films'> & {
  isLoading: boolean;
  scrolledToMovie: number;
};

export const Movies: FC<MoviesProps> = ({ films, isLoading, scrolledToMovie }) => {
  const [selectedMovies, setSelectedMovies] = useMovieFinder(
    s => [s.selectedMovies, s.setSelectedMovies],
    shallow
  );
  const intl = useIntl();
  const { scrollToItem } = useScrollToItem();
  const setSnack = useSnack(s => s.setSnack, shallow);

  const handleToggleSelectMovie = (id, isSelected, isDisabled): void => {
    if (isDisabled) {
      setSnack({
        isOpen: true,
        message: intl.formatMessage({
          id: 'movie_finder.not_available_option',
        }),
        icon: (
          <ImageBoxFuture
            img={notAllowedIcon}
            title={intl.formatMessage({
              id: 'alt.not_allowed_icon',
            })}
            objectFit='contain'
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
        ),
        type: 'info',
      });
      return;
    }
    setSelectedMovies(
      isSelected
        ? selectedMovies.filter(movieId => movieId !== id)
        : [...selectedMovies, id]
    );
  };

  const refsList = films?.reduce((acc, value) => {
    acc[value.id] = createRef();
    return acc;
  }, {});

  useEffect(() => {
    if (!!scrolledToMovie && !!refsList) scrollToItem(refsList[scrolledToMovie]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolledToMovie, refsList]);

  return (
    <Box>
      {(!films || films?.length === 0) && !isLoading && (
        <MovieEmptyState
          title='movie_finder.empty_movies'
          imgWidth={{ tablet: pxToRem(500) }}
        />
      )}
      {films?.length > 0 && (
        <Grid
          container
          columnSpacing={{
            mobile: 2,
            extraLargeDesktop: 7.16,
          }}
          rowSpacing={{
            mobile: 2.98,
            extraLargeDesktop: 9.75,
          }}
        >
          {films?.map(
            (
              {
                id,
                title,
                movieLang,
                image,
                genres,
                bookmark,
                rate,
                isDisabled,
                isAdvancedBooking,
              },
              index
            ) => {
              const isSelected = selectedMovies.includes(id);

              return (
                <Fade
                  key={id}
                  in={true}
                  timeout={300}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  ref={refsList[id]}
                >
                  <Grid item mobile={6} tablet={4} desktop={3}>
                    <FinderMovieCard
                      title={title}
                      language={movieLang}
                      img={image}
                      genres={genres?.map(genre => genre.name)}
                      bookmark={bookmark}
                      isAdvancedBooking={isAdvancedBooking}
                      rate={rate}
                      onClick={() => handleToggleSelectMovie(id, isSelected, isDisabled)}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                    />
                  </Grid>
                </Fade>
              );
            }
          )}
          {isLoading}
        </Grid>
      )}
      {isLoading && !films?.length && <MovieListLoader />}
    </Box>
  );
};
